import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

import Button from "../../UI/Button";
import { useModalStore } from "../../../store/report/modalStore";
import { useReportMutations } from "../../../hooks/reports/mutations/useReportMutations";
import {
  useReportDefinitionQuery,
  useReportStalenessQuery,
} from "../../../hooks/reports/queries/useReportQueries";
import {
  APPLY_DRAFT,
  DELETE_REPORT,
  REVERT_DRAFT,
  SAVE_AS_NEW,
} from "../../../constants/reportActions";
import { formatJSONDateWithoutTime } from "../../../utils/dateHelpers";

const ReportHeader = ({ isLocked }: { isLocked: boolean }) => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { data: currentReport } = useReportDefinitionQuery(reportId ?? "");
  const { actionMutation } = useReportMutations();
  const openModal = useModalStore((s) => s.openModal);
  const { refetch: checkStaleness } = useReportStalenessQuery(
    currentReport?.id ?? "",
    false,
  );

  const [isApplying, setIsApplying] = useState(false);

  const handleApplyDraft = async () => {
    if (!currentReport) return;

    setIsApplying(true);
    try {
      const { data: staleness } = await checkStaleness();

      if (staleness?.isStale) {
        openModal("staleCheck", {
          isStale: true,
          parentReportId: staleness.parentReportId ?? "",
          reportId: currentReport.id,
        });
      } else {
        const result = await actionMutation.mutateAsync({
          id: currentReport.id,
          action: APPLY_DRAFT,
        });
        if (result?.id) navigate(`/reports/${result.id}`, { replace: true });
      }
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <div className="report-header-title">
        <h1>{currentReport?.name ?? "Run a Report"}</h1>
        {isLocked && (
          <span
            className="refresh-lock-badge"
            title="This report's group is currently being refreshed. Edits are temporarily disabled."
          >
            <i className="ri-loader-4-line spin" />
            Refreshing...
          </span>
        )}
      </div>

      {currentReport && (
        <div className="report-metadata">
          <div className="row justify-content-between align-items-center">
            <p className="text-normal margin-0">
              <span className="text-bold">Created:</span>{" "}
              {formatJSONDateWithoutTime(currentReport.created, "yyyy-MM-dd")}
              <span className="margin-horizontal-2">|</span>
              <span className="text-bold">Last Run:</span>{" "}
              {formatDistanceToNow(new Date(currentReport.lastRun), {
                addSuffix: true,
              })}
            </p>

            <div className="report-header-actions">
              <Button
                id="rename-report"
                className="button secondary"
                text="Rename"
                disabled={isLocked}
                onClick={() =>
                  openModal("renameReport", {
                    id: currentReport.id,
                    name: currentReport.name,
                  })
                }
              />

              {currentReport.isDraft && (
                <>
                  <Button
                    id="apply-draft-report"
                    className="button"
                    text="Apply Draft"
                    disabled={isLocked}
                    loading={isApplying}
                    onClick={handleApplyDraft}
                  />
                  <Button
                    id="save-as-new-report"
                    className="button"
                    text="Save As New"
                    disabled={isLocked}
                    onClick={() =>
                      actionMutation.mutate({
                        id: currentReport.id,
                        action: SAVE_AS_NEW,
                        name: currentReport.name,
                      })
                    }
                  />
                </>
              )}

              <Button
                id="delete-report"
                className="button warning"
                text={currentReport.isDraft ? "Revert" : "Delete"}
                disabled={isLocked}
                onClick={() =>
                  openModal("deleteReport", {
                    id: currentReport.id,
                    action: currentReport.isDraft ? REVERT_DRAFT : DELETE_REPORT,
                    parentReportId: currentReport.parentReportId,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportHeader;
